import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff, Mail, Phone } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Header with logos component
const LogosHeader = () => (
  <div className="flex items-center justify-center gap-6 py-6 bg-white shadow-sm">
    <img 
      src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/mvf8jz61_branding.png" 
      alt="Guinée"
      className="h-16 object-contain"
    />
    <img 
      src="https://customer-assets.emergentagent.com/job_workbilan-guinee/artifacts/0ps83xz6_WhatsApp%20Image%202025-08-28%20at%2011.04.26.jpeg" 
      alt="Ministère du Travail et de la Fonction Publique"
      className="h-20 object-contain"
    />
    <img 
      src="https://customer-assets.emergentagent.com/job_igt-portal-1/artifacts/jl6j0vv2_t%C3%A9l%C3%A9chargement.jpeg" 
      alt="Programme Simandou 2040"
      className="h-16 object-contain"
    />
  </div>
);

// Footer component
const PageFooter = () => (
  <footer className="bg-white border-t border-slate-200 py-4 px-6">
    <div className="max-w-md mx-auto text-center space-y-3">
      <div className="flex items-center justify-center gap-6 text-sm text-slate-700">
        <a href="tel:+224613139393" className="flex items-center gap-1 hover:text-primary">
          <Phone className="w-4 h-4" />
          +224 613 13 93 93
        </a>
        <a href="mailto:info@igt.gov.gn" className="flex items-center gap-1 hover:text-primary">
          <Mail className="w-4 h-4" />
          info@igt.gov.gn
        </a>
      </div>
      <p className="text-xs text-slate-500">
        © {new Date().getFullYear()} Inspection Générale du Travail - République de Guinée
      </p>
      <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded">
        <strong>Note:</strong> Seuls les cabinets habilités par l'IGT ont accès à cette plateforme.
      </p>
    </div>
  </footer>
);

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Password strength indicator
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const strengthLabels = ["Très faible", "Faible", "Moyen", "Fort", "Très fort"];
  const strengthColors = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-400", "bg-green-600"];

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Lien de réinitialisation invalide");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
        setValidToken(true);
        setEmail(response.data.email);
      } catch (err) {
        setError(err.response?.data?.detail || "Lien invalide ou expiré");
        setValidToken(false);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success("Mot de passe réinitialisé avec succès !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
        <LogosHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-slate-600">Vérification du lien...</p>
          </div>
        </div>
        <PageFooter />
      </div>
    );
  }

  if (error || !validToken) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
        <LogosHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-xl text-slate-900">Lien invalide</CardTitle>
              <CardDescription>
                {error || "Ce lien de réinitialisation est invalide ou a expiré."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/mot-de-passe-oublie">
                <Button className="w-full bg-primary">
                  Demander un nouveau lien
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
        <PageFooter />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
        <LogosHeader />
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-xl text-slate-900">Mot de passe réinitialisé !</CardTitle>
              <CardDescription>
                Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                className="w-full bg-primary"
                onClick={() => navigate("/login")}
              >
                Se connecter
              </Button>
            </CardContent>
          </Card>
        </div>
        <PageFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      <LogosHeader />
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-xl text-slate-900">Nouveau mot de passe</CardTitle>
            <CardDescription>
              Créez un nouveau mot de passe pour <strong>{email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {/* Password strength indicator */}
                {newPassword && (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-colors ${
                            i < passwordStrength ? strengthColors[passwordStrength - 1] : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">
                      Force: {strengthLabels[passwordStrength - 1] || "Très faible"}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="reset-confirm-password"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={submitting || newPassword !== confirmPassword || newPassword.length < 8}
                data-testid="reset-password-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Réinitialisation...
                  </>
                ) : (
                  "Réinitialiser le mot de passe"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <PageFooter />
    </div>
  );
}
